<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      width="600px"
      v-if="isMoveModalVisible"
      scrollable
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint"
    >
      <v-card>
        <v-toolbar elevation="0">
          <v-toolbar-title class="ml-4 moveModalTitle">
            {{
              $t('message.moveFiles', {
                count: movedItems.length,
              })
            }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              :disabled="isFetching"
              icon
              @click="handleClose()"
              class="mr-2"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-input
          hide-details="auto"
          class="mx-8 pb-4"
          prepend-inner-icon="panda-search"
          v-model="searchQuery"
          :placeholder="
            $t('message.pages.videosDataTable.searchBar.folderPlaceholder')
          "
        ></v-input>
        <v-toolbar v-if="breadcrumbArray.length > 1" elevation="0" class="mx-5">
          <template v-if="isNamesTooBig">
            <v-menu offset-y max-width="250">
              <template v-slot:activator="{ on, attrs }">
                <span class="body-bold-small" v-bind="attrs" v-on="on">
                  {{ $t('message.pages.video.pageMenu.videos') }}
                </span>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in reducedBreadcrumbArray"
                  :key="index"
                  @click="handleBackTo(item)"
                  dense
                >
                  <v-list-item-title
                    :title="
                      item.id
                        ? item.name
                        : $t('message.pages.video.pageMenu.videos')
                    "
                  >
                    <span class="subtitle-2">
                      <v-icon class="mr-2 pb-1" color="#ABADBA">
                        {{ item.id ? 'mdi-folder' : 'mdi-home' }}
                      </v-icon>
                      {{
                        item.id
                          ? item.name
                          : $t('message.pages.video.pageMenu.videos')
                      }}
                    </span>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="body-regular-small textType6--text ml-1"> / </span>
            <button
              v-if="!selectedFolder"
              type="button"
              class="body-regular-small"
              @click="handleBackTo(lastBreadcrumbItem)"
            >
              <span class="body-regular-small">
                {{
                  lastBreadcrumbItem.id
                    ? lastBreadcrumbItem.name
                    : $t('message.pages.video.pageMenu.videos')
                }}
              </span>
            </button>
            <button v-else type="button" class="body-regular-small">
              <span class="primary--text textType6--text">
                {{ selectedFolder.name }}
              </span>
            </button>
          </template>
          <template v-else-if="breadcrumbArray.length > 1">
            <small v-for="(item, i) in breadcrumbArray" :key="i">
              <button type="button" @click="handleBackTo(item)">
                <span class="body-regular-small">
                  <span
                    :class="{
                      'body-bold-small': true,
                      'primary--text textType6--text':
                        i === breadcrumbArray.length - 1,
                    }"
                  >
                    {{
                      item.id
                        ? item.name
                        : $t('message.pages.video.pageMenu.videos')
                    }}
                  </span>
                  <span
                    v-if="i < breadcrumbArray.length - 1"
                    class="body-regular-small textType6--text mx-1"
                  >
                    /
                  </span>
                </span>
              </button>
            </small>
          </template>
        </v-toolbar>

        <v-card-text style="height: 450px">
          <v-list v-if="filteredFolders.length" dense>
            <v-list-item-group v-model="selectedFolder">
              <v-list-item
                class="borderBottom"
                v-for="folder in filteredFolders"
                :key="folder.id"
                :value="folder"
                @click="handleRowClick(folder)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon size="16px" color="#ABADBA"> mdi-folder </v-icon>
                </v-list-item-icon>
                <v-list-item-content class="moveItem">
                  <v-list-item-title v-text="folder.name"></v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="my-0 py-0">
                  <v-icon class="arrowIcon" size="20">mdi-arrow-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <v-row v-else no-gutters align="center" justify="center">
            <span class="mt-8">{{ $t('message.noFoldersFound') }}</span>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-8 py-4">
          <template>
            <span
              style="
                color: var(--v-anchor-base);
                font-size: 14px;
                cursor: pointer;
              "
              @click=";(newFolderMenu = true), displayNoneInMoveModal()"
            >
              {{ $t('message.newFolder') }}
            </span>

            <v-dialog
              v-model="newFolderMenu"
              width="544px"
              height="262px"
              persistent
              scrollable
            >
              <v-card flat class="my-6 mx-6">
                <v-row no-gutters>
                  <v-col class="mb-10" cols="12">
                    <span class="body-bold-medium">{{
                      $t('message.newFolder')
                    }}</span>
                  </v-col>
                  <v-col cols="12">
                    <v-form
                      ref="form"
                      v-model="valid"
                      lazy-validation
                      v-on:submit.prevent
                    >
                      <v-input
                        v-model="newFolder.name"
                        autofocus
                        dense
                        :label="$t('message.name')"
                        :placeholder="$t('message.name')"
                        :rules="[
                          rules.required,
                          rules.maxLength,
                          rules.minLength,
                        ]"
                        required
                        counter="50"
                        color="primary"
                        outlined
                        @keyup.enter="handleNewFolder"
                      ></v-input>
                    </v-form>
                  </v-col>
                </v-row>
                <v-divider class="my-6"></v-divider>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <base-btn
                    type="tertiary"
                    class="mr-2"
                    small
                    text
                    :disabled="fetchingNewFolder"
                    @click="
                      ;(newFolderMenu = false), returnDisplayInMoveModal()
                    "
                  >
                    {{ $t('message.cancel') }}
                  </base-btn>
                  <base-btn
                    type="primary"
                    small
                    text
                    :loading="fetchingNewFolder"
                    :disabled="
                      fetchingNewFolder || !valid || newFolder.name.length < 3
                    "
                    @click="handleNewFolder"
                  >
                    {{ $t('message.create') }}
                  </base-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          <v-spacer></v-spacer>
          <base-btn
            v-if="!isMobileBreakpoint"
            type="tertiary"
            class="mr-2"
            text
            @click="handleClose"
            :disabled="isFetching"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <base-btn
            type="primary"
            @click="handleMove"
            :disabled="isMoveDisabled"
            :loading="isFetching"
          >
            <span>{{ $t('message.move') }}</span>
          </base-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import videosApi from '../services/videosApi'
import foldersApi from '../services/foldersApi'
import i18n from '@/translations/lang'

export default {
  name: 'MoveModal',
  components: {},
  data: () => ({
    videosRoot: { name: 'Videos', id: null },
    open: null,
    searchQuery: '',
    selectedFolder: null,
    newFolderMenu: false,
    movedItems: [],
    isFetching: false,
    isMoveModalVisible: true,
    lastFolder: {
      name: '',
      id: null,
      parent_folder_id: null,
    },
    lastClickedItem: null,
    isAlreadyClicked: false,
    alreadyClickedTimeout: null,
    breadcrumbArray: [{ name: '', id: null, parent_folder_id: null }],
    newFolder: {
      name: '',
    },
    valid: false,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        (v && v.length <= 50) || i18n.t('message.maximumLength'),
      minLength: (v) => (v && v.length > 2) || i18n.t('message.minimumLength'),
    },
    fetchingNewFolder: false,
    oldFolderId: '',
  }),
  computed: {
    ...mapState('user', {
      folders: 'mainFolders',
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    filterSelectedFolder() {
      if (this.$route.params.id) {
        return this.folders.filter(
          (folder) => folder.id != this.$route.params.id
        )
      } else {
        return this.folders
      }
    },
    toBeSelectedFolders() {
      let selectedFolders = this.folders.filter((folder) => {
        return folder.parent_folder_id === this.lastFolder.id
      })
      let itemsThatAreFolders = this.movedItems.filter((movedItem) => {
        return movedItem.name
      })
      for (let folderItem of itemsThatAreFolders) {
        let foundItem = selectedFolders.find((item) => {
          return item.id === folderItem.id
        })
        if (foundItem) {
          let filtered = selectedFolders.filter(
            (item) => item.id !== folderItem.id
          )
          selectedFolders = [...filtered]
        }
      }
      return selectedFolders
    },
    filteredFolders() {
      if (!this.searchQuery) {
        return this.toBeSelectedFolders
      }
      const query = this.searchQuery.toLowerCase()
      return this.toBeSelectedFolders.filter((folder) =>
        folder.name.toLowerCase().includes(query)
      )
    },
    isNamesTooBig() {
      let size = 0
      for (let folder of this.breadcrumbArray) {
        size += folder.name.length
      }
      if (this.selectedFolder?.name) {
        size += this.selectedFolder.name.length
      }
      return size > 40
    },
    lastBreadcrumbItem() {
      return this.breadcrumbArray[this.breadcrumbArray.length - 1]
    },
    reducedBreadcrumbArray() {
      let arrayMod = this.breadcrumbArray.filter((folder) => {
        return folder.id !== this.lastFolder.id
      })
      if (this.selectedFolder) {
        return this.breadcrumbArray
      }
      return arrayMod
    },
    isMoveDisabled() {
      if (this.isFetching) {
        return true
      }
      return false
    },
  },
  watch: {
    isAlreadyClicked: function () {
      this.alreadyClickedTimeout && clearTimeout(this.alreadyClickedTimeout)
      this.alreadyClickedTimeout = setTimeout(() => {
        this.isAlreadyClicked = false
      }, 300)
    },
    newFolderMenu: function () {
      this.newFolder.name = ''
      const refForm = this.$refs.form
      refForm && refForm.resetValidation()
    },
    open(val) {
      if (val === false) this.$root.$emit('MoveModalHasClosed')
    },
  },
  mounted() {
    this.$root.$on('moveModal', (selectedItems) => {
      Object.assign(this.$data, this.initialState())
      this.movedItems = selectedItems
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('moveModal')
  },
  methods: {
    ...mapActions('user', ['editFolder', 'addFolder']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR', 'UPDATE_MAIN_FOLDERS']),
    async handleMove() {
      this.isFetching = true
      let toBeMovedItems = []
      let toBeMovedFolders = []
      let newParentFolderId = this.lastFolder.id
      if (this.selectedFolder) newParentFolderId = this.selectedFolder.id
      for (let moveItem of this.movedItems) {
        if (moveItem.title) {
          toBeMovedItems.push({
            video_id: moveItem.id,
            folder_id: newParentFolderId,
          })
          this.oldFolderId = moveItem.folder_id
        }
        if (moveItem.name) {
          toBeMovedFolders.push(moveItem)
        }
      }
      if (toBeMovedItems.length > 0) {
        await this.moveVideoToFolder(toBeMovedItems)
      }
      if (toBeMovedFolders.length > 0) {
        await this.updateFolders(toBeMovedFolders)
      }

      const countMoved = toBeMovedItems.length + toBeMovedFolders.length
      this.$root.$emit('showMoveSuccess', countMoved)
      this.isFetching = false
      this.handleClose()
    },
    async moveVideoToFolder(videos) {
      try {
        const ret = await videosApi.updateMultipleVideos(videos)
        let updatedVideos = []
        if (videos.length < 2) {
          updatedVideos.push(ret.data)
        } else {
          updatedVideos = ret.data
        }
        if (this.oldFolderId) {
          let foldersResult = this.folders.map((folder) =>
            folder.id === this.oldFolderId
              ? {
                  ...folder,
                  videos_count: folder.videos_count - updatedVideos.length,
                }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
        }
        let addedId = updatedVideos[0].folder_id
        if (addedId) {
          let foldersResult = this.folders.map((folder) =>
            folder.id === addedId
              ? {
                  ...folder,
                  videos_count:
                    parseInt(folder.videos_count) +
                    parseInt(updatedVideos.length),
                }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
        }
        this.$root.$emit('moveFilesVideosPage', updatedVideos)
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
    },
    async updateFolders(folders) {
      let newParentFolderId = this.lastFolder.id
      if (this.selectedFolder) newParentFolderId = this.selectedFolder.id
      for (let i = 0; i < folders.length; i++) {
        try {
          let ret = await foldersApi.updateFolder(
            { parent_folder_id: newParentFolderId },
            folders[i].id
          )

          const foldersResult = this.folders.map((folder) =>
            folder.id === folders[i].id
              ? { ...folder, videos_count: folders[i].videos_count }
              : folder
          )
          this.UPDATE_MAIN_FOLDERS(foldersResult)
          this.editFolder({
            ...ret.data,
            videos_count: folders[i].videos_count,
          })
          this.$root.$emit('removeFolderVideosPage', {
            ...ret.data,
            videos_count: folders[i].videos_count,
          })
        } catch (e) {
          console.error(e)
        }
      }
    },
    handleBackFolder() {
      if (this.lastFolder.parent_folder_id === null) {
        this.lastFolder = {
          name: '',
          id: null,
          parent_folder_id: null,
        }
        return
      }
      let parentFolder = this.folders.find(
        (folder) => folder.id === this.lastFolder.parent_folder_id
      )
      this.lastFolder = parentFolder
    },
    handleBackTo(backToFolder) {
      this.selectedFolder = null
      this.lastFolder = backToFolder
      if (!backToFolder.id) {
        this.breadcrumbArray = [{ name: '', id: null, parent_folder_id: null }]
        return
      }
      if (
        this.breadcrumbArray.length < 2 ||
        this.breadcrumbArray[this.breadcrumbArray.length - 1].id ===
          backToFolder.id
      )
        return
      let index = this.breadcrumbArray.findIndex((folder) => {
        return folder.id === backToFolder.id
      })
      let newArr = this.breadcrumbArray.slice(0, index + 1)
      this.breadcrumbArray = newArr
    },
    handleRowClick(item) {
      return this.handleEnterFolder(item)
    },
    handleEnterFolder(folder) {
      this.lastFolder = folder
      this.breadcrumbArray.push(folder)
    },
    handleClose() {
      this.open = null
      this.$root.$emit('resetIsAnyOtherModal')
    },
    async handleNewFolder() {
      this.fetchingNewFolder = true
      let folderObject = {
        name: this.newFolder.name.trim(),
      }
      if (this.lastFolder.id) {
        folderObject['parent_folder_id'] = this.lastFolder.id
      }
      try {
        let ret = await foldersApi.createFolder(folderObject)
        this.addFolder(ret.data)
        this.$root.$emit('addFolderVideosPage', ret.data)
        this.handleEnterFolder(ret.data)
        this.returnDisplayInMoveModal()
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
      this.fetchingNewFolder = false
      this.newFolderMenu = false
    },
    checkIfHasChildren(folder) {
      let anySon = this.folders.find(
        (item) => item.parent_folder_id === folder.id
      )
      return anySon
    },
    displayNoneInMoveModal() {
      const el = document.querySelector('.v-dialog--active')
      el.style.display = 'none'
    },
    returnDisplayInMoveModal() {
      const el = document.querySelector('.v-dialog--active')
      el.style.display = 'block'
    },
    initialState() {
      return {
        videosRoot: { name: 'Videos', id: null },
        open: null,
        selectedFolder: null,
        newFolderMenu: false,
        movedItems: [],
        isFetching: false,
        lastFolder: {
          name: '',
          id: null,
          parent_folder_id: null,
        },
        lastClickedItem: null,
        isAlreadyClicked: false,
        alreadyClickedTimeout: null,
        breadcrumbArray: [{ name: '', id: null, parent_folder_id: null }],
        newFolder: {
          name: '',
        },
        valid: false,
        rules: {
          required: (v) => (v && !!v) || i18n.t('message.requiredField'),
          maxLength: (v) =>
            (v && v.length <= 50) || i18n.t('message.maximumLength'),
          minLength: (v) =>
            (v && v.length > 2) || i18n.t('message.minimumLength'),
        },
        fetchingNewFolder: false,
      }
    },
  },
}
</script>

<style>
.breadcrumbTexts {
  font-family: Poppins;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.05em;
  color: #878787;
}
</style>

<style lang="scss" scoped>
.moveModalTitle {
  color: varFigma(neutral-20-dark);
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 21.6px */
}

::v-deep .v-list-item--dense,
.v-list--dense .v-list-item {
  min-height: 53px !important;
}

::v-deep .v-list-item--dense .v-list-item__icon,
.v-list--dense .v-list-item .v-list-item__icon {
  height: 53px !important;
  margin-top: 0px !important;
  margin-bottom: 0px !important;
}
.mdi-arrow-right::before {
  color: varFigma(neutral-20-dark);
}

.v-dialog__content {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .v-dialog {
  background-color: varFigma(neutral-100-white) !important;
}

::v-deep .panda-search:before {
  font-size: 16px !important;
}

.borderBottom {
  border-bottom: 1px solid varFigma(neutral-95-lightest);
}

.moveItem {
  color: varFigma(neutral-30-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
}

.moveModalDescription {
  color: varFigma(neutral-40-light);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}

.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: 16px;
}
</style>
